import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { mockData } from '../data/mock';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Clock, User, Calendar } from 'lucide-react';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const service = mockData.services.find(s => s.id === serviceId);

  if (!service) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
          <Button asChild>
            <Link to="/">Go Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  const relatedProjects = mockData.portfolio.filter(p => p.service === serviceId).slice(0, 3);

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-20 px-4 bg-primary-lime">
        <div className="container mx-auto max-w-4xl">
          <Button variant="outline" className="mb-8" asChild>
            <Link to="/">
              <ArrowLeft className="mr-2" size={16} />
              Back to Home
            </Link>
          </Button>
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-black mb-6">
            {service.name}
          </h1>
          <p className="text-2xl text-gray-700 leading-relaxed">
            {service.fullDescription}
          </p>
          <div className="mt-8 flex items-center gap-4">
            <Badge className="bg-black text-white text-base px-4 py-2">
              Starting at {service.startingPrice}
            </Badge>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-serif text-4xl font-bold text-black mb-12">
            What's Included
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {service.features.map((feature, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-card-lime rounded-lg">
                <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm font-bold">{index + 1}</span>
                </div>
                <span className="text-gray-800 font-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-serif text-4xl font-bold text-black mb-8">
            Technologies We Use
          </h2>
          <div className="flex flex-wrap gap-4">
            {service.technologies.map((tech, index) => (
              <Badge key={index} className="bg-black text-white text-base px-6 py-3">
                {tech}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-20 px-4 bg-white">
          <div className="container mx-auto max-w-6xl">
            <h2 className="font-serif text-4xl font-bold text-black mb-12">
              Related Projects
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedProjects.map(project => (
                <div key={project.id} className="bg-card-lime p-6 rounded-lg">
                  <h3 className="font-serif text-2xl font-bold text-black mb-2">
                    {project.title}
                  </h3>
                  <p className="text-gray-700 mb-4">{project.description}</p>
                  <Button variant="link" className="text-black p-0" asChild>
                    <Link to="/portfolio">View Portfolio</Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 px-4 bg-primary-lime">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-serif text-4xl font-bold text-black mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-700 mb-8">
            Let's discuss your {service.name.toLowerCase()} project
          </p>
          <Button className="bg-black text-white hover:bg-gray-800 px-8 py-6 text-lg" asChild>
            <Link to="/#contact">Request a Quote</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
